import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import {
  Plus,
  Search,
  RefreshCw,
  Users,
  ChevronUp,
  ChevronDown,
  CalendarClock,
  Bot,
  X,
  CheckCircle2,
  CircleDashed,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { captureEdgeFunctionFailure, reportUiError } from '../../sentry';
import ConnectionModal, { type Connection } from './ConnectionModal';
import CalendlyMeetingsModal, {
  meetingToConnectionDraft,
  type CalendlyMeeting,
} from './CalendlyMeetingsModal';
import { extractLinkedinProfileSlug } from '../../lib/linkedinCsvImport';
import { normalizeConnectionEmbeddingsStatus } from '../../lib/connectionEmbeddings';

const TABLE_COLUMNS: { label: string; key: keyof Connection }[] = [
  { label: 'Contact', key: 'full_name' },
  { label: 'ICP', key: 'icp' },
  { label: 'Last contact', key: 'last_contact_at' },
];

function linkedinAvatarSrc(linkedinUrl: string | null | undefined): string | null {
  const slug = extractLinkedinProfileSlug(linkedinUrl ?? undefined);
  if (!slug) return null;
  return `https://unavatar.io/linkedin.com/in/${encodeURIComponent(slug)}`;
}

function nameInitials(fullName: string | null | undefined): string {
  const parts = (fullName ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return `${parts[0]![0] ?? ''}${parts[parts.length - 1]![0] ?? ''}`.toUpperCase() || '?';
}

const ConnectionAvatar: React.FC<{ linkedinUrl?: string | null; fullName?: string | null }> = ({
  linkedinUrl,
  fullName,
}) => {
  const [failed, setFailed] = useState(false);
  const src = linkedinAvatarSrc(linkedinUrl ?? null);
  const initials = nameInitials(fullName ?? null);

  if (!src || failed) {
    return (
      <div
        className="h-10 w-10 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-xs font-semibold text-gray-600 dark:text-gray-300 shrink-0"
        aria-hidden
      >
        {initials}
      </div>
    );
  }

  return (
    <img
      src={src}
      alt=""
      width={40}
      height={40}
      className="h-10 w-10 rounded-full object-cover shrink-0 ring-1 ring-gray-200 dark:ring-gray-600 bg-gray-100 dark:bg-gray-700"
      loading="lazy"
      referrerPolicy="no-referrer"
      onError={() => setFailed(true)}
    />
  );
};

function formatLastContact(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return '—';
  }
}

function effectiveSortTime(c: Connection): number {
  if (c.last_contact_at) return new Date(c.last_contact_at).getTime();
  return new Date(c.created_at).getTime();
}

const EmbeddingStatusIcon: React.FC<{ status: string | null | undefined }> = ({ status }) => {
  const s = normalizeConnectionEmbeddingsStatus(status);
  const label =
    s === 'ready'
      ? 'Vectors indexed'
      : s === 'pending'
        ? 'Indexing vectors…'
        : s === 'error'
          ? 'Vector index failed'
          : 'Vectors not indexed';
  if (s === 'ready') {
    return (
      <span title={label} className="inline-flex shrink-0" aria-label={label}>
        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
      </span>
    );
  }
  if (s === 'pending') {
    return (
      <span title={label} className="inline-flex shrink-0" aria-label={label}>
        <Loader2 className="h-3.5 w-3.5 animate-spin text-amber-500" />
      </span>
    );
  }
  if (s === 'error') {
    return (
      <span title={label} className="inline-flex shrink-0" aria-label={label}>
        <AlertCircle className="h-3.5 w-3.5 text-red-500" />
      </span>
    );
  }
  return (
    <span title={label} className="inline-flex shrink-0 text-gray-400 dark:text-gray-500" aria-label={label}>
      <CircleDashed className="h-3.5 w-3.5" />
    </span>
  );
};

const ConnectionsView: React.FC = () => {
  const [connections, setConnections] = useState<Connection[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCalendlyModalOpen, setIsCalendlyModalOpen] = useState(false);
  const [currentConnection, setCurrentConnection] = useState<Connection | null>(null);
  const [connectionDraft, setConnectionDraft] = useState<Partial<Connection> | null>(null);
  const [sortConfig, setSortConfig] = useState<{
    column: keyof Connection;
    direction: 'asc' | 'desc';
  } | null>(null);

  const aiDropdownRef = useRef<HTMLDivElement>(null);
  const [aiDropdownOpen, setAiDropdownOpen] = useState(false);
  const [semanticQuery, setSemanticQuery] = useState('');
  const [semanticLoading, setSemanticLoading] = useState(false);
  /** When non-null, table shows only these connection IDs in this order (semantic rank). */
  const [aiFilteredIds, setAiFilteredIds] = useState<number[] | null>(null);
  const [aiSimilarity, setAiSimilarity] = useState<Record<number, number>>({});

  const clearAiFilter = useCallback(() => {
    setAiFilteredIds(null);
    setAiSimilarity({});
    setSemanticQuery('');
  }, []);

  useEffect(() => {
    if (!aiDropdownOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      const el = aiDropdownRef.current;
      if (el && !el.contains(e.target as Node)) {
        setAiDropdownOpen(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [aiDropdownOpen]);

  useEffect(() => {
    if (!aiDropdownOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAiDropdownOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [aiDropdownOpen]);

  const runSemanticSearch = async () => {
    const q = semanticQuery.trim();
    if (!q) {
      toast.error('Describe who you are looking for (or paste an ICP).');
      return;
    }
    setSemanticLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke<{
        matches?: { connection: Connection; similarity: number }[];
        error?: string;
      }>('search-connections-semantic', {
        body: { query: q, field: 'all_vectors', match_count: 50 },
      });
      if (error) {
        throw new Error(error.message);
      }
      if (data && typeof data === 'object' && 'error' in data && typeof data.error === 'string') {
        throw new Error(data.error);
      }
      const list = (data?.matches ?? []) as { connection: Connection; similarity: number }[];
      const ids = list.map(m => m.connection.id);
      const sim: Record<number, number> = {};
      for (const m of list) {
        sim[m.connection.id] = m.similarity;
      }
      setAiFilteredIds(ids);
      setAiSimilarity(sim);
      setAiDropdownOpen(false);
      if (list.length === 0) {
        toast.message('No matches yet', {
          description:
            'Add ICP / call-me-when text on contacts and save; embeddings refresh in the background.',
        });
      } else {
        toast.success(`Showing ${list.length} match${list.length === 1 ? '' : 'es'}`);
      }
    } catch (e: unknown) {
      captureEdgeFunctionFailure('search-connections-semantic', 'search', e);
      toast.error(e instanceof Error ? e.message : 'Semantic search failed');
    } finally {
      setSemanticLoading(false);
    }
  };

  const existingEmails = useMemo(
    () => new Set(connections.map(c => c.email_address.trim().toLowerCase()).filter(Boolean)),
    [connections]
  );

  const fetchConnections = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('connections')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      setConnections(data || []);
    } catch (error: unknown) {
      reportUiError('connections', 'list', error);
      toast.error(`Error fetching connections: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setConnections([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConnections();
  }, []);

  const aiFilterActive = aiFilteredIds !== null;

  const filteredAndSortedConnections = useMemo(() => {
    let rows: Connection[];

    if (aiFilteredIds !== null) {
      if (aiFilteredIds.length === 0) {
        rows = [];
      } else {
        const byId = new Map(connections.map(c => [c.id, c]));
        rows = aiFilteredIds.map(id => byId.get(id)).filter((c): c is Connection => c != null);
      }
    } else {
      rows = [...connections];
    }

    const q = searchQuery.trim().toLowerCase();
    if (q) {
      rows = rows.filter(connection => {
        const name = connection.full_name?.toLowerCase() ?? '';
        const email = connection.email_address?.toLowerCase() ?? '';
        return name.includes(q) || email.includes(q);
      });
    }

    if (aiFilterActive) {
      return rows;
    }

    return rows.sort((a, b) => {
      if (!sortConfig) {
        return effectiveSortTime(b) - effectiveSortTime(a);
      }

      const col = sortConfig.column as keyof Connection;
      const aValue = a[col];
      const bValue = b[col];

      if (col === 'last_contact_at' || col === 'created_at' || col === 'updated_at') {
        const ta = aValue ? new Date(aValue as string).getTime() : 0;
        const tb = bValue ? new Date(bValue as string).getTime() : 0;
        return sortConfig.direction === 'asc' ? ta - tb : tb - ta;
      }

      if (aValue === null || aValue === undefined) return sortConfig.direction === 'asc' ? -1 : 1;
      if (bValue === null || bValue === undefined) return sortConfig.direction === 'asc' ? 1 : -1;

      const as = String(aValue);
      const bs = String(bValue);
      return sortConfig.direction === 'asc' ? (as > bs ? 1 : as < bs ? -1 : 0) : (as < bs ? 1 : as > bs ? -1 : 0);
    });
  }, [connections, aiFilteredIds, aiFilterActive, searchQuery, sortConfig]);

  const handleSort = (column: keyof Connection) => {
    if (aiFilterActive) {
      toast.message('Column sort is disabled while AI filter is on', {
        description: 'Clear the AI filter below the toolbar to sort the full list.',
      });
      return;
    }
    setSortConfig(current => {
      if (current?.column === column) {
        return current.direction === 'asc'
          ? { column, direction: 'desc' }
          : null;
      }
      return { column, direction: 'asc' };
    });
  };

  const handleAddConnection = () => {
    setCurrentConnection(null);
    setConnectionDraft(null);
    setIsModalOpen(true);
  };

  const handleEditConnection = (connection: Connection) => {
    setCurrentConnection(connection);
    setConnectionDraft(null);
    setIsModalOpen(true);
  };

  const handleCalendlyImport = (meeting: CalendlyMeeting) => {
    const emailLower = meeting.email.trim().toLowerCase();
    if (existingEmails.has(emailLower)) {
      toast.error('This contact is already a connection');
      return;
    }
    setCurrentConnection(null);
    setConnectionDraft(meetingToConnectionDraft(meeting));
    setIsModalOpen(true);
  };

  const tableColSpan = aiFilterActive ? 5 : 4;

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
            <Users className="w-8 h-8 mr-3 text-blue-500" />
            Connections
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Manage your professional connections and contacts
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="search"
              placeholder="Search…"
              className="pl-10 pr-4 py-2.5 w-64 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>
          <button
            type="button"
            onClick={() => setIsCalendlyModalOpen(true)}
            className="flex items-center px-4 py-2.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors font-medium"
          >
            <CalendarClock className="w-5 h-5 mr-2 text-blue-500" />
            Calendly
          </button>
          <div className="relative" ref={aiDropdownRef}>
            <button
              type="button"
              onClick={() => setAiDropdownOpen(v => !v)}
              title="Filter by AI similarity"
              aria-expanded={aiDropdownOpen}
              aria-haspopup="dialog"
              className={`flex items-center gap-2 px-3 py-2.5 border rounded-lg transition-colors font-medium ${
                aiFilterActive || aiDropdownOpen
                  ? 'border-violet-500 bg-violet-50 dark:bg-violet-950/40 text-violet-800 dark:text-violet-200'
                  : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <Bot className="h-5 w-5 shrink-0 text-violet-500" />
              <span>AI</span>
              <ChevronDown
                className={`h-4 w-4 shrink-0 text-gray-500 transition-transform ${aiDropdownOpen ? 'rotate-180' : ''}`}
              />
            </button>
            {aiDropdownOpen ? (
              <div
                className="absolute right-0 top-full z-40 mt-2 w-[min(calc(100vw-2rem),22rem)] rounded-xl border border-gray-200 bg-white p-4 shadow-lg dark:border-gray-600 dark:bg-gray-800"
                role="dialog"
                aria-label="AI filter"
              >
                <p className="text-xs text-gray-500 dark:text-gray-400 leading-snug mb-3">
                  We compare your text to each contact&apos;s <strong>ICP</strong> and all three{' '}
                  <strong>call me when</strong> embeddings and rank by the <strong>strongest</strong> match among
                  those four. Results filter the table below.
                </p>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Description
                </label>
                <textarea
                  value={semanticQuery}
                  onChange={e => setSemanticQuery(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                      e.preventDefault();
                      if (!semanticLoading && semanticQuery.trim()) void runSemanticSearch();
                    }
                  }}
                  rows={3}
                  placeholder="Who are you looking for? (⌘/Ctrl+Enter)"
                  className="w-full resize-y rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-500/30 dark:border-gray-600 dark:bg-gray-900 dark:text-white disabled:opacity-50"
                  disabled={semanticLoading}
                />
                <button
                  type="button"
                  onClick={() => void runSemanticSearch()}
                  disabled={semanticLoading || !semanticQuery.trim()}
                  className="mt-3 w-full rounded-lg bg-violet-600 px-3 py-2.5 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50"
                >
                  {semanticLoading ? 'Searching…' : 'Apply filter'}
                </button>
              </div>
            ) : null}
          </div>
          <button
            type="button"
            onClick={handleAddConnection}
            className="flex items-center px-4 py-2.5 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors font-medium"
          >
            <Plus className="w-5 h-5 mr-2" />
            New Connection
          </button>
        </div>
      </div>

      {aiFilterActive ? (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-violet-200 bg-violet-50/90 px-4 py-2.5 dark:border-violet-900/50 dark:bg-violet-950/30">
          <p className="text-sm text-violet-900 dark:text-violet-100">
            <span className="font-medium">AI filter on:</span>{' '}
            {aiFilteredIds!.length === 0
              ? 'No indexed matches — try another description or clear filter.'
              : `Showing ${filteredAndSortedConnections.length} contact${
                  filteredAndSortedConnections.length === 1 ? '' : 's'
                } (best of ICP + 3 call-me-when per row). Name/email search still applies.`}
          </p>
          <button
            type="button"
            onClick={clearAiFilter}
            className="inline-flex items-center gap-1 rounded-lg border border-violet-300 bg-white px-2.5 py-1.5 text-xs font-medium text-violet-800 hover:bg-violet-50 dark:border-violet-700 dark:bg-gray-900 dark:text-violet-200 dark:hover:bg-violet-950/50"
          >
            <X className="h-3.5 w-3.5" />
            Clear AI filter
          </button>
        </div>
      ) : null}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                {aiFilterActive ? (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-[5.5rem]">
                    Match
                  </th>
                ) : null}
                {TABLE_COLUMNS.map(({ label, key }) => (
                  <th
                    key={String(key)}
                    className={`px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider ${
                      aiFilterActive ? 'cursor-default opacity-80' : 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600'
                    }`}
                    onClick={() => handleSort(key)}
                  >
                    <div className="flex items-center space-x-1">
                      <span>{label}</span>
                      {!aiFilterActive && sortConfig?.column === key ? (
                        sortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                      ) : null}
                    </div>
                  </th>
                ))}
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan={tableColSpan} className="px-6 py-4 text-center">
                    <RefreshCw className="w-6 h-6 animate-spin text-blue-500 mx-auto" />
                  </td>
                </tr>
              ) : filteredAndSortedConnections.length === 0 ? (
                <tr>
                  <td colSpan={tableColSpan} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                    No connections found
                  </td>
                </tr>
              ) : (
                filteredAndSortedConnections.map((connection) => (
                  <tr
                    key={connection.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    {aiFilterActive ? (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-medium tabular-nums text-violet-700 dark:text-violet-300">
                          {Math.round(
                            Math.min(1, Math.max(0, aiSimilarity[connection.id] ?? 0)) * 100
                          )}
                          %
                        </span>
                      </td>
                    ) : null}
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 min-w-0">
                        <EmbeddingStatusIcon status={connection.connection_embeddings_status} />
                        <ConnectionAvatar linkedinUrl={connection.linkedin_url} fullName={connection.full_name} />
                        <div className="min-w-0 flex-1 flex items-start justify-between gap-4">
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {connection.full_name || '—'}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 break-all">
                              {connection.email_address || '—'}
                            </div>
                          </div>
                          <div className="text-sm text-gray-600 dark:text-gray-300 shrink-0 text-right max-w-[45%] truncate">
                            {connection.role?.trim() ? connection.role : '—'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {connection.icp || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {formatLastContact(connection.last_contact_at)}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-4">
                        <Link
                          to={`/connections/${connection.id}`}
                          className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                        >
                          View
                        </Link>
                        <button
                          type="button"
                          onClick={() => handleEditConnection(connection)}
                          className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                        >
                          Edit
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <CalendlyMeetingsModal
        isOpen={isCalendlyModalOpen}
        onClose={() => setIsCalendlyModalOpen(false)}
        existingEmails={existingEmails}
        onImport={handleCalendlyImport}
      />

      <ConnectionModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setConnectionDraft(null);
          setCurrentConnection(null);
        }}
        connection={currentConnection}
        initialDraft={connectionDraft}
        onSuccess={fetchConnections}
      />
    </div>
  );
};

export default ConnectionsView;
